// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../../context/FirebaseConfig';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import logo_circle from '../../assets/logos/logo_uc_blues_on_white.svg';
import eyeIcon from '../../assets/icons/darkblue/fluent--eye-12-filled.png';

const ResetPasswordAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // States for toggling password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // If no mode or oobCode, redirect to login
  useEffect(() => {
    if (!mode || !oobCode) {
      navigate('/login');
    } else if (mode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setEmail(email);
        })
        .catch(() => {
          setError('Der Link ist ungültig oder abgelaufen.');
        });
    }
  }, [mode, oobCode, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      setMessage(''); // Clear any previous success message
      return;
    }
    confirmPasswordReset(auth, oobCode, newPassword)
      .then(() => {
        setMessage('Ihr Passwort wurde erfolgreich zurückgesetzt.');
        setError(''); // Clear any previous error message
      })
      .catch(() => {
        setError('Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es erneut.');
        setMessage(''); // Clear any previous success message
    });
  };

  const isButtonDisabled =
    newPassword === '' || confirmPassword === '' || newPassword !== confirmPassword;

  const passwordMismatch =
    confirmPassword && newPassword !== confirmPassword;

  return (
    <div className="formContainer">
      <div className="formWrapper">
        <img className="logo" src={logo_circle} alt="UpCircle Logo" />
        <span className="title">Passwort zurücksetzen</span>
        {error && (
          <>
            <span className="error">{error}</span>
            <p className="reset-message">
              Click here to <a href="/login">Login</a>
            </p>
          </>
        )}
        {!error && !message && (
          <>
            {email && (
              <p style={{ fontSize: '12px', color: '#16192c', margin: '10px 0' }}>
                Für: {email}
              </p>
            )}
            <form onSubmit={handleSubmit}>
              <div className="input-container">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Neues Passwort"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="input-with-peek"
                />
                {newPassword && (
                  <img
                    src={eyeIcon}
                    alt="Passwort anzeigen"
                    className="peek-icon"
                    onMouseDown={() => setShowNewPassword(true)}
                    onMouseUp={() => setShowNewPassword(false)}
                    onMouseLeave={() => setShowNewPassword(false)}
                  />
                )}
              </div>
              <div className="input-container confirm">
              <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Passwort bestätigen"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="input-with-peek"
              />
              {confirmPassword && (
                  <img
                  src={eyeIcon}
                  alt="Passwort anzeigen"
                  className="peek-icon"
                  onMouseDown={() => setShowConfirmPassword(true)}
                  onMouseUp={() => setShowConfirmPassword(false)}
                  onMouseLeave={() => setShowConfirmPassword(false)}
                  />
              )}
              </div>
              {passwordMismatch && (
                  <p className="password-mismatch-error">
                  Die Passwörter stimmen nicht überein.
                  </p>
              )}
            <button type="submit" disabled={isButtonDisabled}>
              Passwort ändern
            </button>
            </form>
            <p className="reset-message">
              Or click here to <a href="/login">Login</a>
            </p>
          </>
        )}
        {message && (
          <>
            <span className="reset-success">{message}</span>
            <button
              className="login-button"
              onClick={() => navigate('/login')}
            >
              Log In
            </button>
          </>
        )}
      </div>
      <div className="upcircleFooter">
        <span className="brand">UpCircle Technology AG</span>
        <span className="year">{new Date().getFullYear()}</span>
      </div>
    </div>
  );
};

export default ResetPasswordAction;
